import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import {
  fetchNotifications as fetchNotificationsApi,
  fetchUnreadCount as fetchUnreadCountApi,
  markNotificationRead as markNotificationReadApi,
  markAllNotificationsRead as markAllNotificationsReadApi,
  clearAllNotifications as clearAllNotificationsApi,
  NotificationDto,
} from "../../api/notifications";
import {
  requestAgentAccess as requestAgentAccessApi,
  fetchMyAccessRequests as fetchMyAccessRequestsApi,
  fetchPendingRequests as fetchPendingRequestsApi,
  approveAccessRequest as approveAccessRequestApi,
  rejectAccessRequest as rejectAccessRequestApi,
  AgentAccessRequestDto,
  MyAccessRequestDto,
} from "../../api/accessRequests";
import { useShell } from "./ShellContext";

const POLL_INTERVAL_MS = 30_000;

interface NotificationContextValue {
  notifications: NotificationDto[];
  unreadCount: number;
  loading: boolean;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  // Admin: pending access requests
  pendingRequests: AgentAccessRequestDto[];
  approveRequest: (requestId: string) => Promise<void>;
  rejectRequest: (requestId: string) => Promise<void>;
  refreshPendingRequests: () => Promise<void>;
  // User: own request statuses
  myRequestStatuses: Map<string, string>; // agentId → status
  requestAccess: (agentId: string) => Promise<void>;
  refreshMyRequests: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { isAdmin, userInfo } = useShell();
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<AgentAccessRequestDto[]>([]);
  const [myRequests, setMyRequests] = useState<MyAccessRequestDto[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAuthenticated = !!userInfo?.clientPrincipal;

  // Derive map of agentId → latest status for the current user
  const myRequestStatuses = new Map<string, string>();
  for (const req of myRequests) {
    // Keep the most recent request status per agent
    const existing = myRequestStatuses.get(req.agentId);
    if (!existing) {
      myRequestStatuses.set(req.agentId, req.status);
    }
  }

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await fetchUnreadCountApi();
      setUnreadCount(count);
    } catch {
      // Silently fail polling
    }
  }, [isAuthenticated]);

  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const data = await fetchNotificationsApi();
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (e) {
      console.warn("Failed to fetch notifications:", e);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshPendingRequests = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;
    try {
      const requests = await fetchPendingRequestsApi("pending");
      setPendingRequests(requests);
    } catch (e) {
      console.warn("Failed to fetch pending requests:", e);
    }
  }, [isAuthenticated, isAdmin]);

  const refreshMyRequests = useCallback(async () => {
    if (!isAuthenticated || isAdmin) return;
    try {
      const requests = await fetchMyAccessRequestsApi();
      setMyRequests(requests);
    } catch (e) {
      console.warn("Failed to fetch my access requests:", e);
    }
  }, [isAuthenticated, isAdmin]);

  const markRead = useCallback(async (notificationId: string) => {
    try {
      await markNotificationReadApi(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      console.warn("Failed to mark notification as read:", e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsReadApi();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (e) {
      console.warn("Failed to mark all notifications as read:", e);
    }
  }, []);

  const clearAll = useCallback(async () => {
    try {
      await clearAllNotificationsApi();
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) {
      console.warn("Failed to clear notifications:", e);
    }
  }, []);

  const approveRequest = useCallback(async (requestId: string) => {
    await approveAccessRequestApi(requestId);
    setPendingRequests((prev) => prev.filter((r) => r.requestId !== requestId));
    await refreshNotifications();
  }, [refreshNotifications]);

  const rejectRequest = useCallback(async (requestId: string) => {
    await rejectAccessRequestApi(requestId);
    setPendingRequests((prev) => prev.filter((r) => r.requestId !== requestId));
    await refreshNotifications();
  }, [refreshNotifications]);

  const requestAccess = useCallback(async (agentId: string) => {
    await requestAgentAccessApi(agentId);
    // Optimistically update local state
    setMyRequests((prev) => [
      { agentId, status: "Pending", requestedAt: new Date().toISOString() },
      ...prev,
    ]);
  }, []);

  // Initial load
  useEffect(() => {
    if (!isAuthenticated) return;
    refreshNotifications();
    if (isAdmin) {
      refreshPendingRequests();
    } else {
      refreshMyRequests();
    }
  }, [isAuthenticated, isAdmin, refreshNotifications, refreshPendingRequests, refreshMyRequests]);

  // Poll unread count
  useEffect(() => {
    if (!isAuthenticated) return;
    pollRef.current = setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isAuthenticated, refreshUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        markRead,
        markAllRead,
        clearAll,
        refreshNotifications,
        pendingRequests,
        approveRequest,
        rejectRequest,
        refreshPendingRequests,
        myRequestStatuses,
        requestAccess,
        refreshMyRequests,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
